/**
 * VibeCheck CLI v2 — Commander Setup
 * Registers all 10 commands with global flags.
 */

import { Command } from 'commander';
import * as fs from 'fs';
import * as path from 'path';
import { colors, icons, brandName, DOCS_URL } from './ui/theme';
import { registerKickoff } from './commands/kickoff';
import { registerScan } from './commands/scan';
import { registerShip } from './commands/ship';
import { registerDoctor } from './commands/doctor';
import { registerMissionpack } from './commands/missionpack';
import { registerReality } from './commands/reality';
import { registerForge } from './commands/forge';
import { registerPolish } from './commands/polish';
import { registerFirewall } from './commands/firewall';
import { registerDocguard } from './commands/docguard';
import { registerAuth } from './commands/auth';
import { registerCommitShield } from './commands/commit-shield';

function getVersion(): string {
  try {
    const pkgPath = path.resolve(__dirname, '..', 'package.json');
    if (fs.existsSync(pkgPath)) {
      return JSON.parse(fs.readFileSync(pkgPath, 'utf-8')).version || '0.0.0';
    }
    // Try one more level up (when running from dist/)
    const pkgPath2 = path.resolve(__dirname, '..', '..', 'package.json');
    if (fs.existsSync(pkgPath2)) {
      return JSON.parse(fs.readFileSync(pkgPath2, 'utf-8')).version || '0.0.0';
    }
  } catch { /* ignore */ }
  return '0.0.0';
}

/** Commands that don't require a subscription (free tier) */
const FREE_COMMANDS = new Set(['scan', 'ship', 'doctor', 'polish', 'auth', 'login', 'logout', 'help', 'version', 'commit-shield']);

/**
 * Verify the user has an active subscription before running a command.
 * Reads VIBECHECK_TOKEN from env or ~/.vibecheck/token file.
 */
async function requireSubscription(commandName: string): Promise<void> {
  if (FREE_COMMANDS.has(commandName)) return;
  if (process.env.VIBECHECK_SKIP_AUTH === '1') return;

  // Try env var first, then token file
  let token = process.env.VIBECHECK_TOKEN;
  if (!token) {
    try {
      const tokenPath = path.join(require('os').homedir(), '.vibecheck', 'token');
      if (fs.existsSync(tokenPath)) {
        token = fs.readFileSync(tokenPath, 'utf-8').trim();
      }
    } catch { /* ignore */ }
  }

  if (!token) {
    console.error(colors.error(`${icons.error} Authentication required.`));
    console.error(colors.dim('  Sign up at https://app.vibecheckai.dev/pricing ($9.99/mo)'));
    console.error(colors.dim('  Then run: vibecheck login'));
    process.exit(1);
  }

  // Verify subscription
  const apiUrl = process.env.VIBECHECK_API_URL || 'https://api.vibecheckai.dev';
  try {
    const isApiKey = token.startsWith('vc_');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (isApiKey) { headers['X-API-Key'] = token; }
    else { headers['Authorization'] = `Bearer ${token}`; }

    const res = await fetch(`${apiUrl}/api/v1/auth/me`, { headers, signal: AbortSignal.timeout(10000) });
    if (!res.ok) {
      console.error(colors.error(`${icons.error} Invalid or expired token. Please sign in again.`));
      console.error(colors.dim('  Run: vibecheck login'));
      process.exit(1);
    }

    const json = await res.json() as { success: boolean; data?: { tier?: string } };
    const tier = json.data?.tier || 'free';
    if (tier === 'free') {
      console.error(colors.error(`${icons.error} Active subscription required.`));
      console.error(colors.dim('  Subscribe at https://app.vibecheckai.dev/pricing ($9.99/mo)'));
      process.exit(1);
    }
  } catch (err: unknown) {
    // Network error — allow offline grace period if token exists
    if (err instanceof Error && err.name === 'TimeoutError') {
      // Timeout — allow offline usage
    }
  }
}

export function createProgram(): Command {
  const version = getVersion();

  const program = new Command()
    .name('vibecheck')
    .version(version, '-V, --version', 'Show version')
    .description(`${brandName()} — AI Code Validation Platform\n  Catch hallucinations, security issues, and quality problems before they ship.`)
    .option('-v, --verbose', 'Verbose output', false)
    .option('--debug', 'Debug mode (show stack traces)', false)
    .option('-q, --quiet', 'Minimal output (errors only)', false)
    .option('--json', 'Output as JSON (for piping/scripting)', false)
    .option('--no-color', 'Disable colored output')
    .option('--config <path>', 'Custom config file path')
    .option('--path <dir>', 'Project directory', process.cwd())
    .hook('preAction', async (thisCommand) => {
      const cmdName = thisCommand.args?.[0] || thisCommand.name();
      await requireSubscription(cmdName);
    })
    .configureHelp({
      sortSubcommands: false,
      sortOptions: false,
    })
    .addHelpText('after', `
  ${colors.bold('EXAMPLES')}
    $ vibecheck                    ${colors.dim('# Interactive menu')}
    $ vibecheck scan               ${colors.dim('# Quick scan current directory')}
    $ vibecheck scan full          ${colors.dim('# Full comprehensive scan')}
    $ vibecheck ship check         ${colors.dim('# Pre-deployment checks')}
    $ vibecheck doctor             ${colors.dim('# Diagnose project issues')}
    $ vibecheck forge component UserProfile  ${colors.dim('# Generate a component')}

  ${colors.bold('DOCS')}
    ${colors.dim(DOCS_URL)}

  ${colors.bold('VERSION')}
    vibecheck/${version}
`);

  // Register all 10 commands
  registerKickoff(program);
  registerScan(program);
  registerShip(program);
  registerDoctor(program);
  registerMissionpack(program);
  registerReality(program);
  registerForge(program);
  registerPolish(program);
  registerFirewall(program);
  registerDocguard(program);
  registerAuth(program);
  registerCommitShield(program);

  return program;
}
